import React, { useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { apiGet } from 'api/service';
import { GREEN, MONO, SEV_COLOR } from './scanUi';
import { whatLabel } from './jobsLogic';
import { STATUS, barScale, counters, defaultSelection, filterRows, headline, pair, seriesLabel, tabCount, toggleRun, TABS } from './scanDiffLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const TONE = { good: GREEN, bad: '#f97316', neutral: '#64748b' };
const shortDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const PAGE = 30;

export default function ScanDiffPage() {
  const [groups, setGroups] = useState([]);
  const [groupKey, setGroupKey] = useState('');
  const [runs, setRuns] = useState([]);
  const [picked, setPicked] = useState([]);
  const [diff, setDiff] = useState(null);
  const [tab, setTab] = useState('all');
  const [openKey, setOpenKey] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiGet('/api/scan-history/').then((d) => {
      const list = (d.groups || []).filter((g) => g.runs >= 2).concat((d.groups || []).filter((g) => g.runs < 2));
      setGroups(list);
      if (list.length) setGroupKey(`${list[0].user_target}:${list[0].scan_type}`);
    }).catch(() => setError('Could not load your scans.')).finally(() => setLoading(false));
  }, []);

  const group = groups.find((g) => `${g.user_target}:${g.scan_type}` === groupKey);
  useEffect(() => {
    setRuns([]); setPicked([]); setDiff(null);
    if (!group) return;
    apiGet(`/api/scan-history/?user_target=${group.user_target}&scan_type=${encodeURIComponent(group.scan_type)}`)
      .then((d) => { setRuns(d.runs || []); setPicked(defaultSelection(d.runs || [])); }).catch(() => setError('Could not load the runs.'));
  }, [groupKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = useMemo(() => pair(picked, runs), [picked, runs]);
  useEffect(() => {
    setDiff(null); setTab('all'); setOpenKey(null); setLimit(PAGE);
    if (!chosen) return undefined;
    let live = true;
    setComparing(true);
    apiGet(`/api/scan-diff/?scan_a=${chosen.baseline.id}&scan_b=${chosen.current.id}`).then((d) => live && setDiff(d)).catch(() => live && setError('The comparison failed.')).finally(() => live && setComparing(false));
    return () => { live = false; };
  }, [chosen?.baseline.id, chosen?.current.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const h = diff ? headline(diff.stats) : null;
  const rows = diff ? filterRows(diff.results, tab) : [];
  const top = barScale(runs);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700}>Scan comparison</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>See what is new, what got fixed and what got worse between two runs of the same scan</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {!loading && !groups.length && <Alert severity="info">Run the same scan at least twice on an address to compare the results.</Alert>}

      {group && (
        <Box sx={{ ...panel, p: 3, mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
            <TextField select size="small" value={groupKey} onChange={(e) => setGroupKey(e.target.value)} sx={{ minWidth: 320, '& .MuiInputBase-root': { fontWeight: 600 } }}>
              {groups.map((g) => <MenuItem key={`${g.user_target}:${g.scan_type}`} value={`${g.user_target}:${g.scan_type}`}>{seriesLabel(g, whatLabel)}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">Click two runs. The newer one is compared with the older one.</Typography>
          </Stack>
          {runs.length < 2 ? <Typography color="text.secondary">This scan has only run once so far. Run it again to see what changed.</Typography> : (
            <>
              <Stack direction="row" alignItems="flex-end" gap={{ xs: 1, md: 2 }} sx={{ height: 170, borderBottom: 1, borderColor: 'divider', px: 0.5 }}>
                {runs.map((r) => {
                  const on = picked.includes(r.id);
                  return (
                    <Box key={r.id} onClick={() => setPicked((p) => toggleRun(p, r.id))} sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 0.6, cursor: 'pointer' }}>
                      <Typography variant="caption" sx={{ fontFamily: MONO, color: on ? '#4ade80' : 'text.secondary' }}>{r.findings}</Typography>
                      <Box sx={{ width: '100%', maxWidth: 58, height: `${Math.max(4, (r.findings / top) * 120)}px`, borderRadius: '6px 6px 0 0', bgcolor: on ? GREEN : alpha('#fff', 0.1), outline: on ? `2px solid ${GREEN}` : 'none', transition: 'background .15s' }} />
                    </Box>
                  );
                })}
              </Stack>
              <Stack direction="row" gap={{ xs: 1, md: 2 }} sx={{ px: 0.5, pt: 0.8 }}>
                {runs.map((r) => <Typography key={r.id} variant="caption" sx={{ flex: 1, textAlign: 'center', color: picked.includes(r.id) ? '#4ade80' : 'text.secondary' }}>{shortDate(r.date)}</Typography>)}
              </Stack>
            </>
          )}
        </Box>
      )}
      {runs.length >= 2 && !chosen && <Alert severity="info" sx={{ mb: 2.5 }}>Pick one more run to compare.</Alert>}
      {comparing && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={26} sx={{ color: GREEN }} /></Box>}
      {diff?.warning && <Alert severity="warning" sx={{ mb: 2.5 }}>{diff.warning}</Alert>}

      {diff && (
        <>
          <Box sx={{ borderRadius: 3, border: 1, borderColor: alpha(TONE[h.tone], 0.5), background: `linear-gradient(135deg, ${alpha(TONE[h.tone], 0.12)}, ${alpha(TONE[h.tone], 0.02)})`, p: { xs: 2.5, md: 3.5 }, mb: 2.5 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: TONE[h.tone] }}>In short</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.35 }}>{h.text}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>{shortDate(chosen.baseline.date)} compared with {shortDate(chosen.current.date)} · {diff.stats.total} findings looked at</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 2, mb: 2.5 }}>
            {counters(diff.stats).map((c) => (
              <Box key={c.key} onClick={() => setTab(c.key === 'open' ? 'open' : c.key)} sx={{ ...panel, p: 2.2, cursor: 'pointer', borderColor: tab === c.key ? c.color : 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{c.label}</Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 700, fontFamily: MONO, color: c.color }}>{c.value}</Typography>
                <Typography variant="caption" color="text.secondary">{c.note}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ ...panel, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ p: 2.5 }}>
              <Typography fontWeight={600}>What changed</Typography>
              <ToggleButtonGroup exclusive size="small" value={tab} onChange={(_, v) => { if (v) { setTab(v); setLimit(PAGE); } }}>
                {TABS.map((t) => <ToggleButton key={t.id} value={t.id} sx={{ textTransform: 'none', px: 1.6, borderRadius: '999px !important', fontWeight: 600 }}>{t.label} {tabCount(diff.results, t.id)}</ToggleButton>)}
              </ToggleButtonGroup>
            </Stack>
            {!rows.length && <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Nothing in this group.</Typography>}
            {rows.slice(0, limit).map((r) => {
              const st = STATUS[r.status] || STATUS.unchanged;
              const f = r.current || r.baseline;
              const key = `${r.status}-${r.id}`;
              return (
                <Box key={key} onClick={() => setOpenKey(openKey === key ? null : key)} sx={{ px: 2.5, py: 1.6, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: alpha(GREEN, 0.03) } }}>
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 4, height: 34, borderRadius: 1, bgcolor: SEV_COLOR[r.severity] || '#64748b', flex: 'none' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>{r.finding}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>{r.status === 'new' ? 'First seen in the newer run' : r.status === 'resolved' ? 'Gone in the newer run' : r.status === 'regressed' ? `Was ${r.previous_severity} in the older run` : r.status === 'improved' ? `Was ${r.previous_severity} in the older run` : 'In both runs'}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', textTransform: 'capitalize' }}>{r.severity}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, px: 1.2, py: 0.5, borderRadius: 1.5, minWidth: 92, textAlign: 'center', bgcolor: alpha(st.color, 0.16), color: st.color }}>{st.label}</Typography>
                  </Stack>
                  {openKey === key && f && (
                    <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: alpha('#000', 0.2) }}>
                      {f.description && <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{String(f.description).slice(0, 700)}</Typography>}
                      {f.evidence && <Box component="pre" sx={{ m: 0, mt: 1, fontFamily: MONO, fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto' }}>{String(f.evidence).slice(0, 800)}</Box>}
                    </Box>
                  )}
                </Box>
              );
            })}
            {rows.length > limit && <Button fullWidth onClick={() => setLimit((n) => n + PAGE)} sx={{ py: 1.5, color: GREEN, fontWeight: 700 }}>Show more ({rows.length - limit} left)</Button>}
          </Box>
        </>
      )}
    </Box>
  );
}
